import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const ActiveCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, running, scheduled
  const navigate = useNavigate();

  useEffect(() => {
    fetchActiveCampaigns();
  }, []);

  const fetchActiveCampaigns = async () => {
    try {
      const response = await api.get('/campaigns');
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'badge-secondary',
      scheduled: 'badge-info',
      running: 'badge-success',
      paused: 'badge-warning',
      completed: 'badge-primary',
      cancelled: 'badge-danger'
    };
    return <span className={`badge ${colors[status] || 'badge-secondary'}`}>{status}</span>;
  };

  const getCampaignTypeIcon = (type) => {
    const icons = {
      email: '📧',
      social: '📱',
      paid_ads: '💰',
      seo: '🔍',
      content: '📝',
      webinar: '🎥',
      event: '🎪',
      influencer: '⭐'
    };
    return icons[type] || '📊';
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const calculateROI = (campaign) => {
    if (!campaign.cost || campaign.cost === 0) return 'N/A';
    const roi = ((campaign.revenue - campaign.cost) / campaign.cost) * 100;
    return `${roi.toFixed(1)}%`;
  };

  const getFilteredCampaigns = () => {
    if (filter === 'all') return campaigns;
    if (filter === 'running') return campaigns.filter(c => c.status === 'running');
    if (filter === 'scheduled') return campaigns.filter(c => c.status === 'scheduled');
    return campaigns;
  };

  const filteredCampaigns = getFilteredCampaigns();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Campaigns
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🚀 Active Campaigns
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              View and manage all marketing campaigns across your projects
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Filter Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid var(--gray-200)',
          marginBottom: '1.5rem'
        }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              flex: 1,
              padding: '1rem',
              border: 'none',
              background: filter === 'all' ? 'var(--primary-50)' : 'transparent',
              color: filter === 'all' ? 'var(--primary-600)' : 'var(--gray-600)',
              fontWeight: filter === 'all' ? '600' : '400',
              borderBottom: filter === 'all' ? '2px solid var(--primary-600)' : 'none',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            All Campaigns ({campaigns.length})
          </button>
          <button
            onClick={() => setFilter('running')}
            style={{
              flex: 1,
              padding: '1rem',
              border: 'none',
              background: filter === 'running' ? 'var(--primary-50)' : 'transparent',
              color: filter === 'running' ? 'var(--primary-600)' : 'var(--gray-600)',
              fontWeight: filter === 'running' ? '600' : '400',
              borderBottom: filter === 'running' ? '2px solid var(--primary-600)' : 'none',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Running ({campaigns.filter(c => c.status === 'running').length})
          </button>
          <button
            onClick={() => setFilter('scheduled')}
            style={{
              flex: 1,
              padding: '1rem',
              border: 'none',
              background: filter === 'scheduled' ? 'var(--primary-50)' : 'transparent',
              color: filter === 'scheduled' ? 'var(--primary-600)' : 'var(--gray-600)',
              fontWeight: filter === 'scheduled' ? '600' : '400',
              borderBottom: filter === 'scheduled' ? '2px solid var(--primary-600)' : 'none',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Scheduled ({campaigns.filter(c => c.status === 'scheduled').length})
          </button>
        </div>

        {/* Campaigns Table */}
        {filteredCampaigns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <h3 style={{ color: 'var(--gray-600)', margin: '0 0 1rem 0' }}>No Campaigns Found</h3>
            <p style={{ color: 'var(--gray-500)', marginBottom: '2rem' }}>
              {filter === 'all'
                ? 'Create your first campaign to get started with marketing.'
                : `No ${filter} campaigns at the moment.`}
            </p>
            <button
              onClick={() => navigate('/companies')}
              style={{
                backgroundColor: 'var(--primary-600)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Go to Companies & Projects
            </button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Campaign
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Project / Company
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Type
                </th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Status
                </th>
                <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Budget
                </th>
                <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Spent
                </th>
                <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  ROI
                </th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.map((campaign, index) => (
                <tr
                  key={campaign.id}
                  style={{
                    borderBottom: index < filteredCampaigns.length - 1 ? '1px solid var(--gray-100)' : 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  onClick={() => {
                    if (campaign.company_id && campaign.project_id) {
                      navigate(`/companies/${campaign.company_id}/projects/${campaign.project_id}/campaigns`);
                    }
                  }}
                >
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '1.25rem' }}>{getCampaignTypeIcon(campaign.campaign_type)}</span>
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.125rem' }}>
                          {campaign.name}
                        </div>
                        {campaign.description && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {campaign.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ color: 'var(--gray-900)', fontSize: '0.875rem', fontWeight: '500' }}>
                      {campaign.project_name}
                    </div>
                    <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
                      {campaign.company_name}
                    </div>
                  </td>
                  <td style={{ padding: '1rem', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                    {campaign.campaign_type?.replace('_', ' ')}
                    {campaign.channel && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{campaign.channel}</div>
                    )}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    {getStatusBadge(campaign.status)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: 'var(--gray-900)', fontSize: '0.875rem' }}>
                    {formatCurrency(campaign.budget)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: 'var(--red-600)', fontSize: '0.875rem' }}>
                    {formatCurrency(campaign.cost)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', fontSize: '0.875rem', color: campaign.revenue > campaign.cost ? 'var(--green-600)' : 'var(--red-600)' }}>
                    {calculateROI(campaign)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (campaign.company_id && campaign.project_id) {
                          navigate(`/companies/${campaign.company_id}/projects/${campaign.project_id}/campaigns/${campaign.id}/edit`);
                        }
                      }}
                      className="btn btn-sm btn-outline"
                      style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                    >
                      ✏️ Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ActiveCampaigns;
